import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import { Task } from 'src/app/data/model/task';
import { RECORD_PANEL_KEYS, RecordPanelOpener } from '../../tasks/record-review';
import { RecordPanelHostComponent } from './record-panel-host.component';

/**
 * Opens a record's own side panel -- the bill panel the inbox uses, the supplier panel, the
 * payment panel -- over whatever page the Tasks card is on. Each panel shows the approval block
 * at its foot for the task that opened it. Provided by BookkeepingModule, which declares those
 * panels, as TasksModule's RecordPanelOpener.
 */
@Injectable()
export class RecordPanelService extends RecordPanelOpener {
  private _ref: OverlayRef = null;

  constructor(private _overlay: Overlay, private _injector: Injector) {
    super();
  }

  open(task: Pick<Task, 'panelKey' | 'recordId' | 'recordType'>): boolean {
    if (!task?.recordId || !RECORD_PANEL_KEYS.includes(task.panelKey)) { return false; }
    this.close();

    // A body-level overlay, like the inbox's: immune to ancestor transforms, and its block
    // strategy keeps the page underneath where it was.
    this._ref = this._overlay.create({
      positionStrategy: this._overlay.position().global(),
      scrollStrategy: this._overlay.scrollStrategies.block(),
      hasBackdrop: true,
      backdropClass: 'inbox-detail-backdrop'
    });
    this._ref.backdropClick().subscribe(() => this.close());
    this._ref.keydownEvents().subscribe(e => { if (e.key === 'Escape') { this.close(); } });

    const host = this._ref.attach(new ComponentPortal(RecordPanelHostComponent, null, this._injector)).instance;
    host.panelKey = task.panelKey;
    host.recordId = task.recordId;
    host.closed.subscribe(() => this.close());
    return true;
  }

  close(): void {
    this._ref?.dispose();
    this._ref = null;
  }
}
